import { useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Skeleton } from "../ui/skeleton";
import { toast } from "sonner";
import { Trash2, Plus, Check, Ticket, Pencil } from "lucide-react";

export function CouponsSection() {
  const [coupons, setCoupons] = useState(null);
  const [editCouponId, setEditCouponId] = useState(null);
  const [editCoupon, setEditCoupon] = useState({ discount: "", max_uses: "", expires_at: "" });
  const [cCode, setCCode] = useState("");
  const [cDiscount, setCDiscount] = useState(10);
  const [cMaxUses, setCMaxUses] = useState(0);
  const loadCoupons = () => api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch((e) => toast.error(errText(e)));
  useEffect(() => { loadCoupons(); }, []);
  const createCoupon = async () => {
    if (!cCode.trim()) return toast.error("Enter a code");
    try {
      await api.post("/admin/coupons", { code: cCode.trim(), discount: Number(cDiscount) || 10, max_uses: Number(cMaxUses) || 0 });
      toast.success("Coupon created");
      setCCode(""); setCDiscount(10); setCMaxUses(0);
      loadCoupons();
    } catch (e) { toast.error(errText(e)); }
  };
  const toggleCoupon = async (c) => {
    try {
      await api.put(`/admin/coupons/${c.id}`, { code: c.code, discount: c.discount, max_uses: c.max_uses || 0, expires_at: c.expires_at || "", active: !c.active });
      loadCoupons();
    } catch (e) { toast.error(errText(e)); }
  };
  const startEditCoupon = (c) => {
    setEditCouponId(c.id);
    setEditCoupon({ discount: c.discount, max_uses: c.max_uses || 0, expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "" });
  };
  const saveEditCoupon = async (c) => {
    const disc = Number(editCoupon.discount);
    if (!(disc >= 1 && disc <= 100)) return toast.error("Discount must be 1-100%");
    try {
      await api.put(`/admin/coupons/${c.id}`, {
        code: c.code, discount: disc, max_uses: Number(editCoupon.max_uses) || 0,
        expires_at: editCoupon.expires_at || "", active: c.active,
      });
      toast.success("Coupon updated");
      setEditCouponId(null);
      loadCoupons();
    } catch (e) { toast.error(errText(e)); }
  };
  const deleteCoupon = async (id) => {
    try { await api.delete(`/admin/coupons/${id}`); loadCoupons(); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 mt-6" data-testid="coupons-section">
        <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2 mb-4"><Ticket size={18} className="text-[#F5551E]" /> Promo codes</h3>
        <div className="flex items-end gap-2 flex-wrap mb-4">
          <div>
            <Label className="text-[11px] font-semibold text-slate-500">Code</Label>
            <Input value={cCode} onChange={(e) => setCCode(e.target.value.toUpperCase())} placeholder="SAVE25" data-testid="coupon-code-input" className="rounded-xl h-9 mt-1 uppercase w-36" />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-500">Discount %</Label>
            <Input type="number" min={1} max={100} value={cDiscount} onChange={(e) => setCDiscount(e.target.value)} data-testid="coupon-discount-input" className="rounded-xl h-9 mt-1 w-24" />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-500">Max uses (0 = ∞)</Label>
            <Input type="number" min={0} value={cMaxUses} onChange={(e) => setCMaxUses(e.target.value)} data-testid="coupon-maxuses-input" className="rounded-xl h-9 mt-1 w-28" />
          </div>
          <Button onClick={createCoupon} data-testid="coupon-create-btn" className="rounded-xl h-9 bg-[#F5551E] hover:bg-[#E04812] text-white gap-1"><Plus size={15} /> Add code</Button>
        </div>
        {!coupons ? (
          <Skeleton className="h-16 rounded-xl" />
        ) : coupons.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No promo codes yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="coupons-list">
            {coupons.map((c) => (
              <div key={c.id} className={`rounded-xl border px-3 py-2 ${c.active ? "border-slate-200" : "border-slate-100 opacity-60"}`} data-testid={`coupon-${c.id}`}>
                {editCouponId === c.id ? (
                  <div className="flex items-end gap-2 flex-wrap" data-testid={`coupon-edit-${c.id}`}>
                    <p className="font-mono font-bold text-sm text-[#12234A] self-center">{c.code}</p>
                    <div>
                      <Label className="text-[10px] font-semibold text-slate-500">Discount %</Label>
                      <Input type="number" min={1} max={100} value={editCoupon.discount} onChange={(e) => setEditCoupon((f) => ({ ...f, discount: e.target.value }))} data-testid={`coupon-edit-discount-${c.id}`} className="rounded-lg h-8 mt-0.5 w-20" />
                    </div>
                    <div>
                      <Label className="text-[10px] font-semibold text-slate-500">Max uses</Label>
                      <Input type="number" min={0} value={editCoupon.max_uses} onChange={(e) => setEditCoupon((f) => ({ ...f, max_uses: e.target.value }))} data-testid={`coupon-edit-maxuses-${c.id}`} className="rounded-lg h-8 mt-0.5 w-20" />
                    </div>
                    <div>
                      <Label className="text-[10px] font-semibold text-slate-500">Expires</Label>
                      <Input type="date" value={editCoupon.expires_at} onChange={(e) => setEditCoupon((f) => ({ ...f, expires_at: e.target.value }))} data-testid={`coupon-edit-expires-${c.id}`} className="rounded-lg h-8 mt-0.5 w-36" />
                    </div>
                    <Button onClick={() => saveEditCoupon(c)} size="sm" data-testid={`coupon-edit-save-${c.id}`} className="rounded-lg h-8 bg-[#F5551E] hover:bg-[#E04812] text-white gap-1"><Check size={14} /> Save</Button>
                    <Button onClick={() => setEditCouponId(null)} size="sm" variant="ghost" data-testid={`coupon-edit-cancel-${c.id}`} className="rounded-lg h-8 text-slate-500">Cancel</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="font-mono font-bold text-sm text-[#12234A]">{c.code} <span className="text-[#F5551E]">{c.discount}%</span>{c.expires_at && new Date(c.expires_at) < new Date() && <span className="ml-1.5 align-middle text-[9px] font-bold uppercase tracking-wide bg-red-100 text-red-600 rounded-full px-1.5 py-0.5" data-testid={`coupon-expired-${c.id}`}>Expired</span>}</p>
                      <p className="text-[10px] text-slate-400">{c.uses} used{c.max_uses ? ` / ${c.max_uses}` : ""}{c.expires_at ? ` · expires ${c.expires_at.slice(0, 10)}` : ""}</p>
                    </div>
                    <button onClick={() => startEditCoupon(c)} data-testid={`coupon-edit-btn-${c.id}`} className="text-slate-400 hover:text-[#F5551E]"><Pencil size={14} /></button>
                    <Switch checked={c.active} onCheckedChange={() => toggleCoupon(c)} data-testid={`coupon-toggle-${c.id}`} />
                    <button onClick={() => deleteCoupon(c.id)} data-testid={`coupon-delete-${c.id}`} className="text-red-500 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
  );
}
